// Live KPIs (/kpi/nba/live and /kpi/nba/live/:gameId): a game picker over POR's live-tracked games, and the selected
// game's live team + player sheets, then Guard Lineups (LU1). Without a game in the URL the page picks one: the game in progress, else the latest
// game with a live run, else the next game. While the selected game isn't final the page re-reads every minute, so
// each completed period lands without a reload. Screen only: the team sheet grows past a letter page.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FullScreenButton, Pending, useKpiData, useKpiReady } from "./common";
import {
  defaultLiveGame,
  gameMeta,
  liveGameLabel,
  liveMeta,
  tipTime,
} from "./kpiLib";
import GuardLineupsLivePage from "./GuardLineupsLivePage";
import PlayerKpiPage from "./PlayerKpiPage";
import TeamKpiPage from "./TeamKpiPage";
import type { KpiResponse, LiveGame, LiveGamesResponse } from "./types";

const BASE = "/kpi/nba";
const POLL_MS = 60_000;

function pendingReason(g: LiveGame | undefined, reason?: string) {
  if (g?.state === "scheduled")
    return `Tips off ${tipTime(g) ? `at ${tipTime(g)} PT` : "later"}. The first live KPIs post after the first quarter.`;
  if (g?.state === "in_progress")
    return "In progress. The first live KPIs post after the first quarter.";
  return reason;
}

export default function LivePage() {
  const { gameId: routeGame = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qs = params.toString() ? `?${params.toString()}` : "";
  const [tick, setTick] = useState(0);
  const games = useKpiData<LiveGamesResponse>("api/kpi/live/games", tick);
  const list = useMemo(() => games.data?.games || [], [games.data]);
  const selected = routeGame
    ? decodeURIComponent(routeGame)
    : defaultLiveGame(list)?.gameId || "";
  const game = list.find((g) => g.gameId === selected);
  const targets = params.get("targets");
  const load = useKpiData<KpiResponse>(
    selected
      ? `api/kpi/live/${encodeURIComponent(selected)}` +
          (targets ? `?targets=${encodeURIComponent(targets)}` : "")
      : null,
    tick,
  );
  // A direct link to a game the list doesn't carry (a replay) has no state: it is shown once, not polled.
  const polling = !!game && game.state !== "final";
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(() => setTick((x) => x + 1), POLL_MS);
    return () => clearInterval(t);
  }, [polling]);

  const root = useRef<HTMLDivElement>(null);
  const [fitted, setFitted] = useState(false);
  const onSettled = useCallback((s: boolean) => setFitted(s), []);
  const data = load.data;
  const hasPlayers = (data?.players?.length || 0) > 0;
  const ready = useKpiReady(
    root,
    load,
    data?.status?.state === "ready" && hasPlayers && !fitted,
  );
  const noGames = !!games.data && !list.length && !routeGame;
  // A direct link to a game the list doesn't carry (last season's replay) still shows in the picker.
  const orphan =
    selected && !game
      ? data?.meta.game
        ? liveMeta(gameMeta(data.meta.game), data.meta.live?.throughPeriod).line
        : selected
      : null;

  return (
    <div className="kpi-doc" ref={root} {...ready}>
      <div className="kpi-webbar">
        <Link to={BASE} style={{ fontWeight: 700 }}>
          ← Season KPIs
        </Link>
        <span className="lbl">Game</span>
        <select
          value={selected}
          onChange={(e) =>
            navigate(`${BASE}/live/${encodeURIComponent(e.target.value)}${qs}`)
          }
          aria-label="Game"
          disabled={!list.length && !orphan}
        >
          {orphan && <option value={selected}>{orphan}</option>}
          {list.map((g) => (
            <option key={g.gameId} value={g.gameId}>
              {liveGameLabel(g)}
            </option>
          ))}
        </select>
        {polling && <span className="lbl">Updates every minute</span>}
        <FullScreenButton target={root} />
      </div>
      <FullScreenButton target={root} floating />
      {games.error && !selected && (
        <Pending
          title="Live KPIs"
          reason={`Could not load the games: ${games.error}`}
        />
      )}
      {noGames && (
        <Pending
          title="Live KPIs"
          reason="No live-tracked Portland games this season yet."
        />
      )}
      {load.error && (
        <Pending
          title="Team KPIs · live"
          reason={`Could not load the KPIs: ${load.error}`}
        />
      )}
      {data?.status?.state === "pending" && (
        <Pending
          title="Team KPIs · live"
          reason={pendingReason(game, data.status.reason)}
        />
      )}
      {data?.status?.state === "ready" && data.meta.game && (
        <>
          <TeamKpiPage data={data} dashboard pageLabel="Team KPIs · Live" />
          {hasPlayers && (
            <PlayerKpiPage
              data={data}
              onSettled={onSettled}
              pageLabel="Player KPIs · Live"
              hrefFor={(p) =>
                `${BASE}/packet/${selected}/player/${encodeURIComponent(p.playerId)}${qs}`
              }
            />
          )}
          <GuardLineupsLivePage data={data} />
        </>
      )}
    </div>
  );
}
